import React, { useEffect, useState } from 'react';
import Has from '../../auth/Has';
import { CAN_MODIFY_RISK_RULE } from '../../auth/permissions';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SalvButton, SalvTab, SalvTabList, SalvTabPanel, SalvTabs, Size } from '@salvapp/ui-components';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../../feedback/feedbackReducer';
import { showConfirmationModal } from '../../confirmation-modal/confirmationModalReducer';
import { InternalApiRiskRule, InternalApiScenarioVersionStatus } from '../../api-internal';
import { useLocation, useSearchParams } from 'react-router';
import RiskRuleVersionModal from './RiskRuleVersionModal';
import { useDuplicateRiskRuleVersion, useUpdateRiskRuleVersion } from './riskRuleQueryActions';
import RiskRuleVersionsTable from './RiskRuleVersionsTable';
import './RiskRuleVersions.scss';

const PARAM_TAB = 'tab';

type RiskRuleVersionsProps = {
  riskRule: InternalApiRiskRule;
};

const RiskRuleVersions = ({ riskRule }: RiskRuleVersionsProps): React.ReactElement | null => {
  const [showVersionModal, setShowVersionModal] = useState(false);

  const [versionId, setVersionId] = useState<number | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get(PARAM_TAB) || 'versions';
  const [selectedTab, setSelectedTab] = useState(initialTab);

  const { id: riskRuleId, versions } = riskRule;
  const dispatch = useDispatch();

  const { mutate: updateRiskRuleVersion } = useUpdateRiskRuleVersion();
  const { mutate: duplicateRiskRuleVersion } = useDuplicateRiskRuleVersion();

  const onComplete = async () => {
    setVersionId(null);
    setShowVersionModal(false);
  };

  const location = useLocation();

  useEffect(() => {
    searchParams.set(PARAM_TAB, selectedTab);
    setSearchParams(searchParams, {
      state: location.state,
      replace: true,
    });
  }, [selectedTab]);

  const createVersion = () => {
    setShowVersionModal(true);
    setVersionId(null);
  };
  const editVersion = (id: number) => {
    setShowVersionModal(true);
    setVersionId(id);
  };

  const duplicateToDraft = async (id: number) => {
    duplicateRiskRuleVersion({ handle: riskRuleId, id: id });

    setVersionId(null);
  };

  const deleteVersion = async (id: any) => {
    dispatch(
      showConfirmationModal({
        title: 'Are you sure?',
        description: 'This risk rule version will be deleted.',
        confirmationText: 'Yes, delete it!',
        cancelText: 'No, keep it',
        onConfirm: async () => {
          try {
            updateRiskRuleVersion({
              handle: riskRuleId,
              id: id,
              update: {
                status: InternalApiScenarioVersionStatus.HISTORIC,
              },
            });

            await onComplete();
          } catch (_) {
            dispatch(
              showFeedback({
                message: 'Oops, something went wrong!',
                success: false,
              })
            );
          }
        },
      })
    );
  };

  return (
    <div className='versions-container'>
      <SalvTabs value={selectedTab} onChange={(value) => setSelectedTab(value)}>
        <SalvTabList>
          <SalvTab value='versions' title={`Versions (${versions?.length || 0})`} />
        </SalvTabList>
        <SalvTabPanel value='versions'>
          <div className='d-flex justify-content-end'>
            <Has allPermissions={CAN_MODIFY_RISK_RULE}>
              <SalvButton
                variant='muted'
                size={Size.SMALL}
                onClick={createVersion}
                label={
                  <>
                    <FontAwesomeIcon icon={faPlus} className='me-2' />
                    New version
                  </>
                }
              />
            </Has>
          </div>
          <RiskRuleVersionsTable
            versions={versions || []}
            onDeleteVersion={deleteVersion}
            onEditVersion={editVersion}
            onDuplicateAsDraft={duplicateToDraft}
          />
        </SalvTabPanel>
      </SalvTabs>

      <RiskRuleVersionModal
        showModal={showVersionModal}
        setShowModal={setShowVersionModal}
        versionId={versionId}
        riskRule={riskRule}
      />
    </div>
  );
};

export default RiskRuleVersions;
